import React, { useEffect, useState } from "react";
import { Plus, Archive, Gavel, Pencil } from "lucide-react";
import { reApi, errMsg, fmtDateTime, pretty } from "@/lib/residentApi";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { inputCls, GoldButton, GhostButton, Pill, Empty, ErrorLine, EditToggle } from "./ui";

const AddNote = ({ rid, categories, onDone }) => {
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [error, setError] = useState(null);
  const add = async () => {
    setError(null);
    try { await reApi.post("/case-notes", { resident_id: rid, body, category }); setBody(""); onDone(); } catch (e) { setError(errMsg(e)); }
  };
  return (
    <div className="space-y-2 p-3 border border-white/10 rounded-sm">
      <textarea data-testid="note-input-body" rows={3} value={body} placeholder="Write a case note…" onChange={(e) => setBody(e.target.value)} className={inputCls} />
      <div className="flex items-center gap-2">
        <select data-testid="note-input-category" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} !w-auto`}>
          {categories.map((c) => <option key={c} value={c}>{pretty(c)}</option>)}
        </select>
        <GoldButton data-testid="note-add" onClick={add} disabled={!body.trim()} className="ml-auto"><Plus className="h-3 w-3" /> Add note</GoldButton>
      </div>
      <ErrorLine testId="note-error">{error}</ErrorLine>
    </div>
  );
};

const ArchiveNote = ({ n, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const go = async () => { await reApi.patch(`/case-notes/${n.id}/archive`, { reason }); onDone(); };
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid={`note-archive-${n.id}`} className="ml-auto text-nex-muted hover:text-red-300" aria-label="Archive note"><Archive className="h-3 w-3" /></button>;
  return (
    <span className="ml-auto flex items-center gap-1.5">
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" data-testid={`note-archive-reason-${n.id}`} className={`${inputCls} !py-0.5 !w-40 !text-xs`} />
      <button type="button" disabled={!reason.trim()} onClick={go} data-testid={`note-archive-confirm-${n.id}`} className="font-mono text-[10px] uppercase text-red-300 disabled:opacity-40">Archive</button>
      <button type="button" onClick={() => setOpen(false)} data-testid={`note-archive-cancel-${n.id}`} className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
    </span>
  );
};

function EditNote({ n, categories, onDone, onCancel }) {
  const [body, setBody] = useState(n.body || "");
  const [category, setCategory] = useState(n.category);
  const [error, setError] = useState(null);
  const save = async () => {
    setError(null);
    try { await reApi.patch(`/case-notes/${n.id}`, { body, category }); onDone(); } catch (e) { setError(errMsg(e)); }
  };
  return (
    <li data-testid={`note-edit-${n.id}`} className="p-3 border border-nex-gold/40 rounded-sm space-y-2">
      <textarea data-testid={`note-edit-body-${n.id}`} rows={4} value={body} onChange={(e) => setBody(e.target.value)} className={inputCls} />
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid={`note-edit-category-${n.id}`} value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} !w-auto`}>
          {[...new Set([...categories, n.category])].map((c) => <option key={c} value={c}>{pretty(c)}</option>)}
        </select>
        <span className="text-[11px] text-nex-muted">The original wording is kept in the note's history.</span>
        <GhostButton data-testid={`note-edit-cancel-${n.id}`} onClick={onCancel} className="ml-auto">Cancel</GhostButton>
        <GoldButton data-testid={`note-edit-save-${n.id}`} onClick={save} disabled={!body.trim()}>Save</GoldButton>
      </div>
      <ErrorLine testId={`note-edit-error-${n.id}`}>{error}</ErrorLine>
    </li>
  );
}

const Note = ({ n, manage, onDone, openConduct, editing, onEdit }) => (
  <li data-testid={`note-${n.id}`} className={`p-3 border border-white/5 bg-white/[0.02] rounded-sm ${n.archived ? "opacity-50" : ""}`}>
    <div className="flex flex-wrap items-center gap-2 mb-1.5">
      <Pill>{pretty(n.category)}</Pill>
      {n.label && <span className="font-mono text-[9px] uppercase tracking-wider text-nex-muted">{n.label}</span>}
      <span className="text-[11px] text-nex-muted">{n.author?.name || n.author?.email || "—"} · {fmtDateTime(n.created_at)}{n.archived ? ` · archived: ${n.archive_reason}` : ""}</span>
      {n.incident_id && <button type="button" onClick={openConduct} data-testid={`note-incident-link-${n.id}`} className="inline-flex items-center gap-1 text-[11px] text-nex-gold"><Gavel className="h-3 w-3" /> Incident on Conduct card</button>}
      {n.edited_at && <span className="text-[10px] text-nex-muted">· edited {fmtDateTime(n.edited_at)}</span>}
      {editing && !n.archived && <button type="button" onClick={onEdit} data-testid={`note-edit-open-${n.id}`} className="ml-auto inline-flex items-center gap-1 text-[11px] text-nex-gold"><Pencil className="h-3 w-3" /> Edit</button>}
    </div>
    <p className="text-sm text-nex-silver/90 whitespace-pre-wrap">{n.body}</p>
    {(n.follow_ups || []).map((f, i) => (
      <p key={i} data-testid={`note-followup-${n.id}-${i}`} className="mt-1.5 pl-3 border-l border-nex-gold/30 text-xs text-nex-muted">{f.author || "—"} · {fmtDateTime(f.at)} — {f.body}</p>
    ))}
  </li>
);

export default function NotesTab({ resident, manage = false }) {
  const { openWindow } = useFloatingWindows();
  const [data, setData] = useState({ notes: null, categories: [] });
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const load = () => reApi.get("/case-notes", { resident_id: resident.id, include_archived: archived }).then(setData).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, [resident.id, archived]); // eslint-disable-line react-hooks/exhaustive-deps
  const all = data.notes || [];
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hay = (n) => [n.body, n.label, n.title, n.author?.name, n.author?.email, ...(n.follow_ups || []).map((f) => `${f.author} ${f.body}`)].join(" ").toLowerCase();
  const matches = (n) => words.every((w) => hay(n).includes(w));
  const counts = all.filter((n) => matches(n)).reduce((a, n) => ({ ...a, [n.category]: (a[n.category] || 0) + 1 }), {});
  const notes = all.filter((n) => (filter === "all" || n.category === filter) && matches(n));
  const openConduct = () => openWindow("re_conduct", { title: "Conduct", description: "NCN notices, conduct score (100-point scale) and incident history per resident", resident });
  const chip = (id, text, n) => (
    <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`notes-filter-${id}`}
      className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${filter === id ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>{text} · {n}</button>
  );
  return (
    <div data-testid="notes-tab" className="space-y-4">
      {!manage && <EditToggle editing={editing} setEditing={(v) => { setEditing(v); setEditId(null); }} label="Edit notes" testId="notes" />}
      <AddNote rid={resident.id} categories={data.categories} onDone={load} />
      {!manage && editing && <p data-testid="notes-readonly-note" className="text-[11px] text-nex-muted">Tap Edit on any note to fix its wording or type.</p>}
      <ErrorLine testId="notes-error">{error}</ErrorLine>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes by word or author…" data-testid="notes-search" className={inputCls} />
      {words.length > 0 && <p data-testid="notes-search-count" className="text-[11px] text-nex-muted">{notes.length} matching note{notes.length === 1 ? "" : "s"}</p>}
      <div className="flex flex-wrap items-center gap-1.5">
        {chip("all", "All", all.filter((n) => matches(n)).length)}
        {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([c, n]) => chip(c, pretty(c), n))}
        <label className="ml-auto text-[11px] text-nex-muted inline-flex items-center gap-1.5"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} data-testid="notes-show-archived" /> Show archived</label>
      </div>
      {data.notes && !notes.length ? <Empty testId="notes-empty">{words.length ? "No notes match your search." : "No case notes yet."}</Empty> : (
        <ul className="space-y-2" data-testid="notes-list">{notes.map((n) => (editId === n.id
          ? <EditNote key={n.id} n={n} categories={data.categories} onCancel={() => setEditId(null)} onDone={() => { setEditId(null); load(); }} />
          : <Note key={n.id} n={n} manage={manage} onDone={load} openConduct={openConduct} editing={editing} onEdit={() => setEditId(n.id)} />))}</ul>
      )}
    </div>
  );
}
